import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { importarAvaliacoesGoogle } from "@/lib/admin/avaliacoes.functions";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";

type Avaliacao = Database["public"]["Tables"]["google_reviews"]["Row"];

export const Route = createFileRoute("/admin/avaliacoes")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Avaliações | Painel" },
      { name: "description", content: "Seleção editorial das avaliações exibidas no site do Dr. Thiago Viana." },
      { property: "og:title", content: "Avaliações | Painel" },
      { property: "og:description", content: "Seleção editorial das avaliações exibidas no site do Dr. Thiago Viana." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: Avaliacoes,
});

function Avaliacoes() {
  const [avaliacoes, setAvaliacoes] = useState<Avaliacao[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [mensagem, setMensagem] = useState<string | null>(null);
  const [importando, setImportando] = useState(false);

  useEffect(() => {
    void supabase
      .from("google_reviews")
      .select("*")
      .order("reviewed_at", { ascending: false, nullsFirst: false })
      .then(({ data, error }) => {
        setAvaliacoes(data ?? []);
        setMensagem(error ? "Não foi possível carregar as avaliações." : null);
        setCarregando(false);
      });
  }, []);

  async function importar() {
    setImportando(true);
    setMensagem(null);
    try {
      const resultado = await importarAvaliacoesGoogle();
      const { data } = await supabase
        .from("google_reviews")
        .select("*")
        .order("reviewed_at", { ascending: false, nullsFirst: false });
      setAvaliacoes(data ?? []);
      setMensagem(
        resultado.importadas === 0
          ? "O Google não devolveu avaliações para este perfil."
          : `${resultado.importadas} avaliações lidas do Google. Escolha quais aparecem no site.`,
      );
    } catch (erro) {
      setMensagem(erro instanceof Error ? erro.message : "Não foi possível buscar as avaliações.");
    } finally {
      setImportando(false);
    }
  }

  async function alternar(avaliacao: Avaliacao) {
    const selected = !avaliacao.selected;
    setMensagem(null);
    const { error } = await supabase
      .from("google_reviews")
      .update({ selected, updated_at: new Date().toISOString() })
      .eq("id", avaliacao.id);

    if (error) {
      setMensagem("Não foi possível alterar a seleção.");
      return;
    }

    setAvaliacoes((atuais) =>
      atuais.map((item) => (item.id === avaliacao.id ? { ...item, selected } : item)),
    );
    setMensagem(selected ? "Avaliação selecionada para o site." : "Avaliação removida do site.");
  }

  return (
    <section className="max-w-3xl">
      <h1 className="text-lg font-semibold text-foreground">Avaliações do Google</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        As avaliações importadas do perfil oficial aparecem aqui. Você decide quais ficam visíveis na home.
      </p>

      <div className="mt-6 rounded-md border border-border bg-muted p-4">
        <p className="text-sm font-medium text-foreground">Buscar avaliações no Google</p>
        <p className="mt-1 text-sm text-muted-foreground">
          O Google entrega até cinco avaliações públicas por busca. As novas se somam às já importadas e nenhuma
          aparece no site sem a sua escolha.
        </p>
        <Button className="mt-3" type="button" disabled={importando} onClick={() => void importar()}>
          {importando ? "Buscando..." : "Buscar avaliações agora"}
        </Button>
      </div>

      {mensagem ? <p className="mt-4 text-sm text-foreground">{mensagem}</p> : null}
      {carregando ? <p className="mt-6 text-sm text-muted-foreground">Carregando...</p> : null}
      {!carregando && avaliacoes.length === 0 ? (
        <p className="mt-6 rounded-md border border-dashed border-border p-6 text-sm text-muted-foreground">
          Nenhuma avaliação foi importada ainda.
        </p>
      ) : null}

      <ul className="mt-6 space-y-3">
        {avaliacoes.map((avaliacao) => (
          <li className="rounded-md border border-border p-4" key={avaliacao.id}>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-foreground">{avaliacao.author_name}</p>
                <p className="mt-1 text-sm text-muted-foreground">{avaliacao.review_text}</p>
              </div>
              <Button
                type="button"
                variant={avaliacao.selected ? "secondary" : "outline"}
                onClick={() => void alternar(avaliacao)}
              >
                {avaliacao.selected ? "Remover do site" : "Mostrar no site"}
              </Button>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}